import dayjs from "dayjs";
import { ChevronDown, ChevronUp, CirclePlus, Plus, RefreshCw, UserPlus, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PopoverAnchor } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/date.time.utils";
import ShiftPill from "./ShiftPill";
import {
	ShiftPlannerPendingEntry,
	ShiftPlannerPendingShiftRef,
	ShiftPlannerPendingStatus,
	ShiftPlannerRow,
	ShiftPlannerShift,
} from "./shiftPlanner.types";
import {
	dayTotals,
	groupCellShifts,
	plannerCellKey,
	plannerHeaderKey,
	weekDates,
} from "./shiftPlanner.utils";

export interface ShiftPlannerWeekProps {
	/** Monday of the week, "YYYY-MM-DD". */
	weekStart: string;
	/** The rows of this week block (see buildWeekRows). */
	rows: ShiftPlannerRow[];
	/** The shifts that fall inside this week (day totals, unassigned ids). */
	shifts: ShiftPlannerShift[];
	/** Consultant rows shown before collapsing behind "Visa fler (N)". */
	maxVisibleRows: number;
	/** Compact left column — see {@link ShiftPlannerProps.narrow}. */
	narrow?: boolean;
	/** The planner-wide slot selection (keys from plannerCellKey). */
	selected: Set<string>;
	/** Shifts to outline (id → applied/invited) — a consultant entry in a
	 * pending card is hovered and these are their shifts (see ShiftPill). */
	highlightedShifts: Map<string, ShiftPlannerPendingStatus>;
	/** Resolves a pill's pending-card entries (see ShiftPill). */
	getPendingDetails: (shift: ShiftPlannerShift) => ShiftPlannerPendingEntry[];
	/** Highlight these shifts across the planner ([] clears). */
	onHighlightShifts: (shifts: ShiftPlannerPendingShiftRef[]) => void;
	/** "Boka" on an applied entry in a pill's pending card (see ShiftPill). */
	onAcceptEntry?: (entry: ShiftPlannerPendingEntry) => void;
	/** "Ta bort önskan" on an invited entry in a pill's pending card. */
	onUninviteEntry?: (entry: ShiftPlannerPendingEntry) => void;
	/** The cell (`plannerCellKey`) whose pill's pending card is open —
	 * planner-wide state so only one card is open at a time (cell, not shift:
	 * a multi-invited shift renders on several rows). */
	pendingCardCellKey: string | null;
	onPendingCardOpenChange: (cellKey: string, open: boolean) => void;
	/** Cell the open popover anchors to, if any. */
	editingAnchorKey?: string;
	onCellMouseDown: (row: ShiftPlannerRow, date: string, e: React.MouseEvent) => void;
	onCellMouseEnter: (row: ShiftPlannerRow, date: string) => void;
	/** Click on a pill (`shift` set) or an empty cell (`shift` unset) — with
	 * stacked same-day pills each edits only its own shift. A demand "×N"
	 * pill passes its whole group so the edit covers all N shifts. */
	onCellClick: (
		row: ShiftPlannerRow,
		date: string,
		shift?: ShiftPlannerShift,
		group?: ShiftPlannerShift[]
	) => void;
	/** Double-click on a demand pill — the shortcut straight to the consultant
	 * picker ("Matcha konsult"). Unset = no shortcut (job create has no picker). */
	onCellDoubleClick?: (
		row: ShiftPlannerRow,
		date: string,
		shift: ShiftPlannerShift,
		group: ShiftPlannerShift[]
	) => void;
	/** Demand cells fanned out into individual pills (keys from
	 * plannerCellKey) — view state, toggled via the "×N" capsule. */
	expandedGroupCells: Set<string>;
	onToggleGroupCell: (cellKey: string) => void;
	/** Right-click on a demand pill — open the book/invite context menu at
	 * the pointer. Unset = native context menu. */
	onCellContextMenu?: (
		row: ShiftPlannerRow,
		date: string,
		shift: ShiftPlannerShift,
		group: ShiftPlannerShift[],
		x: number,
		y: number
	) => void;
	/** Mousedown on a day header — Shift toggles the slot and drags
	 * additively; a plain press arms a drag that activates on crossing into
	 * another slot. */
	onHeaderMouseDown: (date: string, e: React.MouseEvent) => void;
	onHeaderMouseEnter: (date: string) => void;
	/** Plain click on a day header — edits the selection when the header is in it. */
	onHeaderClick: (date: string) => void;
	/** Day-header (+) — open the "Lägg till behov" dialog for this day (or
	 * the whole header selection when this day is part of it). */
	onHeaderAdd: (date: string) => void;
	/** Set on EMPTY weeks: whether the label chevron expands the minimized
	 * week into the full (empty) row grid, or collapses it back. Weeks with
	 * shifts always render expanded and get no chevron. */
	expandToggle?: "expand" | "collapse";
	onToggleWeekExpand?: (weekStart: string) => void;
	/** Hover ✕ on a "Välj konsult" row — delete the row: its unbooked shifts,
	 * or, for a still-empty pre-opened lane, the lane itself. */
	onRemoveLane?: (weekStart: string, row: ShiftPlannerRow) => void;
	/** Click on the week label with the click count (`event.detail`) — double
	 * selects the week's headers Mon–Fri, triple widens to Mon–Sun. */
	onWeekLabelClick: (weekStart: string, clickCount: number) => void;
	onRemoveShift: (row: ShiftPlannerRow, date: string, shift: ShiftPlannerShift) => void;
	onRequestConsultantPicker?: (shiftIds: string[]) => void;
	/** The "Obemannad" row link — gets the clicked row's shift ids, but
	 * the planner may widen to the active selection before opening the
	 * picker (which is why this is separate from onRequestConsultantPicker).
	 * `all` is set on a Shift-click ("Välj konsult (alla)"): widen to every
	 * unassigned shift across ALL weeks. */
	onUnassignedRowPick?: (rowShiftIds: string[], all?: boolean) => void;
	/** Every open shift in the whole job — when other weeks hold unmanned
	 * shifts too, the Obemannad label opens a this-week/all-weeks chooser
	 * before the picker. */
	allUnassignedCount: number;
	/** Clear the booked consultant from the given shifts, keeping the shifts. */
	onUnassignConsultant?: (shiftIds: string[]) => void;
}

/**
 * One week block of the ShiftPlanner: the day-header row (with totals, plus a
 * (+) unassigned add in minimized weeks), the "Obemannad" demand row (grouped
 * "×N" pills + the "Lägg till behov" popover), and one row per consultant.
 *
 * Purely presentational: selection, the editing session and the popover live
 * in {@link ShiftPlanner}, so multi-select spans week blocks. This component
 * only renders its rows and forwards cell gestures.
 */
const ShiftPlannerWeek: React.FC<ShiftPlannerWeekProps> = ({
	weekStart,
	rows,
	shifts,
	maxVisibleRows,
	narrow = false,
	selected,
	highlightedShifts,
	getPendingDetails,
	onHighlightShifts,
	onAcceptEntry,
	onUninviteEntry,
	pendingCardCellKey,
	onPendingCardOpenChange,
	editingAnchorKey,
	onCellMouseDown,
	onCellMouseEnter,
	onCellClick,
	onCellDoubleClick,
	expandedGroupCells,
	onToggleGroupCell,
	onCellContextMenu,
	onHeaderMouseDown,
	onHeaderMouseEnter,
	onHeaderClick,
	onHeaderAdd,
	expandToggle,
	onToggleWeekExpand,
	onRemoveLane,
	onWeekLabelClick,
	onRemoveShift,
	onRequestConsultantPicker,
	onUnassignedRowPick,
	allUnassignedCount,
	onUnassignConsultant,
}) => {
	const { t } = useTranslation();

	const [expanded, setExpanded] = useState(false);

	const dates = useMemo(() => weekDates(weekStart), [weekStart]);
	const today = dayjs().format("YYYY-MM-DD");

	const hiddenRowCount = expanded ? 0 : Math.max(0, rows.length - maxVisibleRows);
	const visibleRows = hiddenRowCount > 0 ? rows.slice(0, maxVisibleRows) : rows;

	const shiftsByDate = useMemo(() => {
		const map: Record<string, ShiftPlannerShift[]> = {};
		for (const shift of shifts) (map[shift.date] ??= []).push(shift);
		return map;
	}, [shifts]);

	// Weeks outside the current year show the year so scrolling far can't mislead
	const weekLabel = t(narrow ? "shiftPlanner.weekShort" : "shiftPlanner.week", {
		week: dayjs(weekStart).isoWeek(),
		month: dayjs(weekStart)
			.locale("sv")
			.format(dayjs(weekStart).year() === dayjs().year() ? "MMM" : "MMM YYYY"),
	});

	function renderAnchored(key: string, node: React.ReactElement) {
		return editingAnchorKey === key ? (
			<PopoverAnchor asChild key={key}>
				{node}
			</PopoverAnchor>
		) : (
			node
		);
	}

	// The left (label) column is tighter in narrow mode to give the day columns
	// more room; its cells drop to px-2. Still wide enough for the short label
	// ("v.32 aug") next to the empty-week chevron without ellipsing.
	const labelCellPad = narrow ? "px-2" : "px-3";

	return (
		<div>
			<div
				className={cn(
					"grid select-none gap-1",
					narrow
						? "grid-cols-[minmax(112px,140px)_repeat(7,minmax(0,1fr))]"
						: "grid-cols-[minmax(130px,170px)_repeat(7,minmax(0,1fr))]"
				)}
			>
				{/* Header row — double/triple-click the label selects the week's
				    day headers Mon–Fri / Mon–Sun */}
				<div
					onClick={(e) => onWeekLabelClick(weekStart, e.detail)}
					className={cn(
						"group flex cursor-pointer items-center justify-between gap-1 rounded border border-gray-200 bg-white py-2 text-sm font-semibold",
						labelCellPad
					)}
				>
					<span className="min-w-0 truncate">{weekLabel}</span>
					{/* Subtle expand/collapse for EMPTY weeks — the global rows exist
					    in every week; expanding just reveals their empty cells */}
					{expandToggle && onToggleWeekExpand && (
						<button
							type="button"
							aria-label={t(
								expandToggle === "expand" ? "shiftPlanner.showRows" : "shiftPlanner.hideRows"
							)}
							title={t(
								expandToggle === "expand" ? "shiftPlanner.showRows" : "shiftPlanner.hideRows"
							)}
							onClick={(e) => {
								// The label click is the Mon–Fri/Mon–Sun selection gesture
								e.stopPropagation();
								onToggleWeekExpand(weekStart);
							}}
							className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
						>
							{expandToggle === "expand" ? (
								<ChevronDown className="h-4 w-4" />
							) : (
								<ChevronUp className="h-4 w-4" />
							)}
						</button>
					)}
				</div>
				{dates.map((date, i) => {
					const isWeekend = i >= 5;
					const isToday = date === today;
					const totals = dayTotals(shiftsByDate[date] || []);
					const headerKey = plannerHeaderKey(date);
					const isSelected = selected.has(headerKey);
					return renderAnchored(
						headerKey,
						<div
							key={date}
							data-planner-cell={headerKey}
							data-planner-today={isToday || undefined}
							onMouseDown={(e) => onHeaderMouseDown(date, e)}
							onMouseEnter={() => onHeaderMouseEnter(date)}
							onClick={() => onHeaderClick(date)}
							onClickCapture={(e) => {
								// Shift-clicks are selection gestures — never open the popover
								if (e.shiftKey) {
									e.preventDefault();
									e.stopPropagation();
								}
							}}
							className={cn(
								// The whole header is the (+)'s hit area, so it reads as one
								// button: pointer cursor + the (+) darkening on header hover
								"group cursor-pointer rounded border px-2 py-1.5",
								// Selection wins over the today column marker
								isSelected
									? "border-action bg-action/10"
									: isToday
									? "border-brand ring-1 ring-inset ring-brand bg-white"
									: "border-gray-200 bg-white"
							)}
						>
							<div className="flex items-start justify-between">
								<span
									className={cn("text-sm font-semibold capitalize", isWeekend && "text-danger")}
								>
									{dayjs(date).locale("sv").format("ddd D")}
								</span>
								{/* Every day header carries the add: (+) opens the SAME
								    "Lägg till behov" dialog as cells and header selections —
								    for this day, or the whole header selection when this
								    day is part of it. Clicking anywhere else in the header
								    does the same (see handleHeaderClick); the (+) stays as
								    the visible affordance. */}
								<button
									type="button"
									aria-label={t("shiftPlanner.addDemand") as string}
									title={t("shiftPlanner.addDemand") as string}
									onClick={(e) => {
										// The header's own click targets the selection when this
										// day is part of one — the (+) must not also fire it
										e.stopPropagation();
										onHeaderAdd(date);
									}}
									className="shrink-0 rounded text-gray-400 group-hover:text-gray-600 hover:text-gray-600"
								>
									<CirclePlus className="h-4 w-4" />
								</button>
							</div>
							<div className="text-xs text-gray-500">
								{formatNumber(totals.hours, 1)} h
								{totals.breakHours > 0 &&
									` / ${t("shiftPlanner.breakShort", {
										hours: formatNumber(totals.breakHours, 0),
									})}`}
							</div>
						</div>
					);
				})}

				{/* Rows */}
				{visibleRows.map((row) => (
					<React.Fragment key={row.key}>
						{!row.consultantId ? (
							// The "Obemannad" demand row — the open work sits on top of
							// the grid. The label is the picker link whenever a picker is
							// wired up (job create has none) and is DISABLED while the
							// week has none; when OTHER weeks are unmanned too it first
							// asks "this week or all?". Adding demand lives in the day
							// headers' (+); the hover ✕ deletes the job's whole open
							// demand.
							<div
								className={cn(
									"group flex items-center gap-1 rounded bg-white/50 py-2",
									labelCellPad
								)}
							>
								{!onUnassignedRowPick ? (
									<span className="min-w-0 flex-1 truncate text-sm text-gray-700">
										{t("shiftPlanner.unmanned")}
									</span>
								) : Object.keys(row.shiftByDate).length > 0 ? (
									(() => {
										const weekIds = Object.values(row.shiftByDate)
											.flat()
											.map((shift) => shift.id);
										// Plain like the consultant labels — the row hover
										// reveals a UserPlus to signal "add consultant" (same
										// pattern as the consultant rows' hover controls)
										const labelClasses =
											"flex min-w-0 flex-1 items-center gap-1 text-left text-sm text-gray-800";
										const labelContent = (
											<>
												<span className="truncate">{t("shiftPlanner.unmanned")}</span>
												<UserPlus className="h-3.5 w-3.5 shrink-0 text-gray-400 opacity-0 group-hover:opacity-100" />
											</>
										);
										// Everything unmanned lives in this week — no chooser,
										// straight to the picker
										if (allUnassignedCount <= weekIds.length) {
											return (
												<button
													type="button"
													title={t("shiftPlanner.selectConsultant") as string}
													onClick={() => onUnassignedRowPick?.(weekIds)}
													className={labelClasses}
												>
													{labelContent}
												</button>
											);
										}
										// Other weeks are unmanned too — ask what the picker
										// should cover before opening it
										return (
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<button
														type="button"
														title={t("shiftPlanner.selectConsultant") as string}
														className={labelClasses}
													>
														{labelContent}
													</button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
													<DropdownMenuItem onSelect={() => onUnassignedRowPick?.(weekIds)}>
														{t("shiftPlanner.pickThisWeek", { count: weekIds.length }) as string}
													</DropdownMenuItem>
													<DropdownMenuItem onSelect={() => onUnassignedRowPick?.(weekIds, true)}>
														{
															t("shiftPlanner.pickAllWeeks", {
																count: allUnassignedCount,
															}) as string
														}
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										);
									})()
								) : (
									<button
										type="button"
										disabled
										className="min-w-0 flex-1 cursor-default truncate text-left text-sm text-gray-400"
									>
										{t("shiftPlanner.unmanned")}
									</button>
								)}
								{onRemoveLane && Object.keys(row.shiftByDate).length > 0 && (
									<button
										type="button"
										aria-label={t("shiftPlanner.removeLane") as string}
										title={t("shiftPlanner.removeLane") as string}
										onClick={() => onRemoveLane(weekStart, row)}
										className="shrink-0 text-gray-400 opacity-0 hover:text-danger focus-visible:opacity-100 group-hover:opacity-100"
									>
										<X className="h-4 w-4" />
									</button>
								)}
							</div>
						) : (
							<div
								data-contact={row.consultantId}
								className={cn(
									"group flex items-center gap-1 rounded bg-white/50 py-2",
									labelCellPad
								)}
							>
								<span className="min-w-0 flex-1 truncate text-sm">{row.name}</span>
								{/* Change/unassign the consultant for THIS row's BOOKED shifts
								    of this week only ("Önskad" pills are not bookings — their
								    own ✕ withdraws the invitation) — revealed on row hover */}
								{Object.values(row.shiftByDate)
									.flat()
									.some((shift) => shift.consultantId) && (
									<div className="flex shrink-0 items-center gap-0.5">
										{onRequestConsultantPicker && (
											<button
												type="button"
												aria-label={t("shiftPlanner.changeConsultant") as string}
												title={t("shiftPlanner.changeConsultant") as string}
												onClick={() =>
													onRequestConsultantPicker(
														Object.values(row.shiftByDate)
															.flat()
															.filter((shift) => shift.consultantId)
															.map((shift) => shift.id)
													)
												}
												className="text-gray-400 opacity-0 hover:text-gray-600 focus-visible:opacity-100 group-hover:opacity-100"
											>
												<RefreshCw className="h-3.5 w-3.5" />
											</button>
										)}
										{onUnassignConsultant && (
											<button
												type="button"
												aria-label={t("shiftPlanner.unassignConsultant") as string}
												title={t("shiftPlanner.unassignConsultant") as string}
												onClick={() =>
													onUnassignConsultant(
														Object.values(row.shiftByDate)
															.flat()
															.filter((shift) => shift.consultantId)
															.map((shift) => shift.id)
													)
												}
												className="text-gray-400 opacity-0 hover:text-danger focus-visible:opacity-100 group-hover:opacity-100"
											>
												<X className="h-4 w-4" />
											</button>
										)}
									</div>
								)}
							</div>
						)}

						{dates.map((date) => {
							const key = plannerCellKey(row, date);
							// The demand row groups identical times into "×N" pills —
							// unless the cell is fanned out via its capsule; consultant
							// rows keep one pill per shift
							const isDemandRow = !row.consultantId;
							const cellExpanded = isDemandRow && expandedGroupCells.has(key);
							const pills = groupCellShifts(
								row.shiftByDate[date] ?? [],
								isDemandRow && !cellExpanded
							);
							const isSelected = selected.has(key);
							const isToday = date === today;
							return renderAnchored(
								key,
								<div
									key={date}
									data-planner-cell={key}
									data-today={isToday || undefined}
									onMouseDown={(e) => onCellMouseDown(row, date, e)}
									onMouseEnter={() => onCellMouseEnter(row, date)}
									onClickCapture={(e) => {
										// Shift-clicks are selection gestures — never open the popover
										if (e.shiftKey) {
											e.preventDefault();
											e.stopPropagation();
										}
									}}
									className={cn(
										"rounded border",
										narrow ? "p-0.5" : "p-1",
										// Selection wins over the today marker
										isSelected
											? "border-action bg-action/10"
											: isToday
											? "border-brand ring-1 ring-inset ring-brand bg-white"
											: "border-gray-200 bg-white"
									)}
								>
									{pills.length > 0 ? (
										// Same-day pills of one row stack inside the cell
										<div className="flex h-full flex-col gap-1">
											{pills.map(({ shift, shifts: pillShifts }) => (
												<ShiftPill
													key={shift.id}
													shift={shift}
													count={pillShifts.length}
													// The capsule fans the cell's groups out into
													// individual pills (view state only — identical
													// shifts regroup on collapse)
													onCountClick={() => onToggleGroupCell(key)}
													narrow={narrow}
													// An unassigned shift on a consultant's row = they are
													// invited to it — the amber "Önskad" pill
													variant={
														shift.absence
															? "absence"
															: shift.consultantId
															? "booked"
															: row.consultantId
															? "invited"
															: "unassigned"
													}
													highlighted={highlightedShifts.get(shift.id) ?? false}
													getPendingDetails={getPendingDetails}
													onHighlightShifts={onHighlightShifts}
													onAcceptEntry={onAcceptEntry}
													onUninviteEntry={onUninviteEntry}
													cardOpen={pendingCardCellKey === `${key}:${shift.id}`}
													onCardOpenChange={(open) =>
														onPendingCardOpenChange(`${key}:${shift.id}`, open)
													}
													// An "Önskad" MIRROR pill (open shift on a consultant
													// row) is read-only — its ✕ withdraws the invitation,
													// everything else happens on the demand-row original
													onClick={
														!shift.consultantId && row.consultantId
															? undefined
															: () => onCellClick(row, date, shift, pillShifts)
													}
													// Double-click on a DEMAND pill jumps straight to the
													// consultant picker — the shortcut past the context
													// menu's "Matcha konsult"
													onDoubleClick={
														onCellDoubleClick && isDemandRow && !shift.absence
															? () => onCellDoubleClick(row, date, shift, pillShifts)
															: undefined
													}
													// Right-click: demand pills get the book/invite/actions
													// menu, booked pills their own action menu. Absence and
													// "Önskad" mirror pills keep the native menu.
													onContextMenu={
														onCellContextMenu &&
														!shift.absence &&
														!(row.consultantId && !shift.consultantId)
															? (e) => {
																	e.preventDefault();
																	onCellContextMenu(
																		row,
																		date,
																		shift,
																		pillShifts,
																		e.clientX,
																		e.clientY
																	);
															  }
															: undefined
													}
													// A "×N" pill's ✕ removes ONE shift of the group —
													// the demand count decrements
													onRemove={() => onRemoveShift(row, date, shift)}
												/>
											))}
											{cellExpanded && (
												// Fanned-out cell — the chip collapses identical
												// shifts back into "×N" pills
												<button
													type="button"
													aria-label={t("shiftPlanner.collapseGroup") as string}
													title={t("shiftPlanner.collapseGroup") as string}
													onClick={() => onToggleGroupCell(key)}
													className="mx-auto flex h-4 items-center rounded-full border border-gray-300 bg-white px-1.5 text-gray-500 hover:text-gray-700"
												>
													<ChevronUp className="h-3 w-3" />
												</button>
											)}
										</div>
									) : (
										// Empty cell — in a consultant row a click books that
										// consultant, in the demand row it creates an unassigned
										// shift. The plus stays visible (faint) so cells read as
										// add-targets — they are the primary way to add shifts.
										<button
											type="button"
											onClick={() => onCellClick(row, date)}
											className="group flex h-full min-h-[52px] w-full items-center justify-center rounded hover:bg-gray-50"
										>
											<Plus className="h-4 w-4 text-gray-200 transition-colors group-hover:text-gray-500" />
										</button>
									)}
								</div>
							);
						})}
					</React.Fragment>
				))}
			</div>

			{hiddenRowCount > 0 && (
				<div className="mt-1 rounded bg-gray-100 py-2 text-center">
					<button
						type="button"
						onClick={() => setExpanded(true)}
						className="text-sm text-action underline hover:no-underline"
					>
						{t("shiftPlanner.showMore", { count: hiddenRowCount })}
					</button>
				</div>
			)}
		</div>
	);
};

/** The anchor key only when it points into this week (cell keys and
 * `header:<date>` keys both end in the date). */
function anchorInWeek(weekStart: string, anchorKey?: string): string | undefined {
	return anchorKey && weekDates(weekStart).includes(anchorKey.slice(-10)) ? anchorKey : undefined;
}

/**
 * Selection painting updates the planner-wide `selected` set on every cell the
 * drag crosses; re-rendering all week blocks for each step makes fast drags
 * laggy. This comparator lets a week skip re-rendering unless one of ITS OWN
 * cells changed selection state (or its rows/anchor/handlers changed — the
 * handlers ShiftPlanner passes are identity-stable).
 */
function areWeekPropsEqual(prev: ShiftPlannerWeekProps, next: ShiftPlannerWeekProps): boolean {
	if (
		prev.weekStart !== next.weekStart ||
		prev.rows !== next.rows ||
		prev.shifts !== next.shifts ||
		prev.maxVisibleRows !== next.maxVisibleRows ||
		prev.narrow !== next.narrow ||
		anchorInWeek(prev.weekStart, prev.editingAnchorKey) !==
			anchorInWeek(next.weekStart, next.editingAnchorKey) ||
		prev.onCellMouseDown !== next.onCellMouseDown ||
		prev.onCellMouseEnter !== next.onCellMouseEnter ||
		prev.onCellClick !== next.onCellClick ||
		prev.onCellDoubleClick !== next.onCellDoubleClick ||
		prev.expandedGroupCells !== next.expandedGroupCells ||
		prev.onToggleGroupCell !== next.onToggleGroupCell ||
		prev.onCellContextMenu !== next.onCellContextMenu ||
		prev.onHeaderMouseDown !== next.onHeaderMouseDown ||
		prev.onHeaderMouseEnter !== next.onHeaderMouseEnter ||
		prev.onHeaderClick !== next.onHeaderClick ||
		prev.onHeaderAdd !== next.onHeaderAdd ||
		prev.expandToggle !== next.expandToggle ||
		prev.onToggleWeekExpand !== next.onToggleWeekExpand ||
		prev.onRemoveLane !== next.onRemoveLane ||
		prev.onWeekLabelClick !== next.onWeekLabelClick ||
		prev.onRemoveShift !== next.onRemoveShift ||
		prev.onRequestConsultantPicker !== next.onRequestConsultantPicker ||
		prev.onUnassignedRowPick !== next.onUnassignedRowPick ||
		prev.allUnassignedCount !== next.allUnassignedCount ||
		prev.onUnassignConsultant !== next.onUnassignConsultant ||
		prev.getPendingDetails !== next.getPendingDetails ||
		prev.onHighlightShifts !== next.onHighlightShifts ||
		prev.onAcceptEntry !== next.onAcceptEntry ||
		prev.onUninviteEntry !== next.onUninviteEntry ||
		prev.onPendingCardOpenChange !== next.onPendingCardOpenChange
	) {
		return false;
	}
	// The open pending card is planner-wide state too — only the weeks that
	// hold the previously/newly open CELL need to re-render (card keys are
	// `rowKey:date:shiftId`, so look for the week's dates inside the key)
	if (prev.pendingCardCellKey !== next.pendingCardCellKey) {
		const cardInWeek = (key: string | null) =>
			!!key && weekDates(next.weekStart).some((date) => key.includes(`:${date}`));
		if (cardInWeek(prev.pendingCardCellKey) || cardInWeek(next.pendingCardCellKey)) {
			return false;
		}
	}
	// Pending-card highlights change planner-wide too — only re-render the
	// weeks whose own shifts flip
	if (prev.highlightedShifts !== next.highlightedShifts) {
		for (const shift of next.shifts) {
			if (prev.highlightedShifts.get(shift.id) !== next.highlightedShifts.get(shift.id)) {
				return false;
			}
		}
	}
	if (prev.selected === next.selected) return true;
	for (const date of weekDates(next.weekStart)) {
		const headerKey = plannerHeaderKey(date);
		if (prev.selected.has(headerKey) !== next.selected.has(headerKey)) return false;
		for (const row of next.rows) {
			const key = plannerCellKey(row, date);
			if (prev.selected.has(key) !== next.selected.has(key)) return false;
		}
	}
	return true;
}

export default React.memo(ShiftPlannerWeek, areWeekPropsEqual);
